import React, { useCallback, useEffect, useState } from 'react'
import { GroupButtonContainer, LabelButton, RadioInput } from './styles'

type Value = string | number
export interface IRadioButton {
  id?: string
  value: Value
  label: string
}

export interface RadioButtonsProps {
  options: IRadioButton[]
  onChange: (selected: keyof IRadioButton | IRadioButton | Value) => void
  propertyToBeReturned?: keyof IRadioButton
  value?: Value
  isDisabled?: boolean
}

/**
 * This component will render a group of buttons with the label
 * @param {RadioButtonsProps['options']} options - array of IRadioButton objects
 * @param {RadioButtonsProps['onChange']} onChange - callback with return the selected button returned the value wanted
 * @param {RadioButtonsProps['propertyToBeReturned']} propertyToBeReturned - property wanted from the callback of IRadioButton
 * @param {RadioButtonsProps['value']} value - value corresponding to the selected button
 * @param {RadioButtonsProps['isDisabled']} boolean - if the buttons are disabled
 * @returns {JSX.Element} return the rendered group of buttons
 * @example
 * <GroupButtons returnSelectedState={setSelectedState} labels={['Ano', 'Mês', 'Dia']} />
 */

export const RadioButtons = ({
  options,
  onChange,
  propertyToBeReturned,
  value,
  isDisabled = false
}: RadioButtonsProps): JSX.Element => {
  const [selectedButton, setSelectedButton] = useState<number>(0)

  useEffect(() => {
    const indexOfOptionSelected = options.findIndex(o => o.value === value)
    setSelectedButton(indexOfOptionSelected)
  }, [options, value])

  const handleSelectButton = useCallback(
    (indexOfSelectedButton: number) => {
      if (selectedButton !== indexOfSelectedButton) {
        setSelectedButton(indexOfSelectedButton)
        const optionSelected: IRadioButton = options[indexOfSelectedButton]
        const returnedValue = propertyToBeReturned ? optionSelected[propertyToBeReturned] : optionSelected
        onChange(returnedValue as keyof IRadioButton | IRadioButton)
      }
    },
    [onChange, options, propertyToBeReturned, selectedButton]
  )

  const keyboardShortcuts = (event: React.KeyboardEvent): void => {
    const { key } = event

    switch (key) {
      case 'End':
      case 'PageDown':
        event.preventDefault()
        return handleSelectButton(options.length - 1)
      case 'Home':
      case 'PageUp':
        event.preventDefault()
        return handleSelectButton(0)
    }
  }

  return (
    <GroupButtonContainer onKeyDown={event => keyboardShortcuts(event)} role="group" aria-label="Select one option">
      {options.map((option, index) => {
        const optionId = option.id ?? `radio-element-${index}`

        return (
          <React.Fragment key={index}>
            <RadioInput
              type="radio"
              id={optionId}
              checked={selectedButton === index}
              onChange={() => handleSelectButton(index)}
              aria-selected={selectedButton === index}
              autoComplete="off"
              disabled={isDisabled}
            />
            <LabelButton htmlFor={optionId}>{option.label}</LabelButton>
          </React.Fragment>
        )
      })}
    </GroupButtonContainer>
  )
}
